/**
 * RealAdapter — Backend API implementation.
 *
 * Every method performs an actual HTTP call via the shared realGet/realPost
 * helpers, which handle token injection, X-Request-Id, and error normalization.
 *
 * Endpoint mapping follows the API contract in docs/UI_MINIMAL_API_CONTRACT.md.
 * Response shapes are passed through as-is — no client-side massaging to hide
 * backend contract drift.
 */

import type {
  IDataAdapter,
  PaginatedResponse,
  CompanyItem,
  ExportJobItem,
  JobItem,
  CompaniesInsightsSummary,
  CompaniesRecentActivityItem,
  CompanyListItem,
  CompanyDetails,
  DashboardMetric,
  MasterCompanyItem,
  UserItem,
  ListCompaniesParams,
  ListExportJobsParams,
  ListJobsParams,
  ListCompanyListParams,
  ListMasterCompaniesParams,
  ListUsersParams,
  CreateExportJobInput,
  CreateCompanyInput,
  CreateCompanyResult,
  CreateImportJobInput,
  ImportJobItem,
  CreateIngestionJobInput,
  UnicornPredictionItem,
  UnicornPredictionSummary,
  UnicornCapturePerformance,
  UnicornPredictionMethodology,
} from "./IDataAdapter"
import { realDelete, realGet, realPatch, realPost } from "./realHttp"

export const RealAdapter: IDataAdapter = {
  // ---------------------------------------------------------------------------
  // Companies
  // ---------------------------------------------------------------------------

  createCompany(input: CreateCompanyInput): Promise<CreateCompanyResult> {
    return realPost("/api/companies", input)
  },

  listCompanies(
    params?: ListCompaniesParams
  ): Promise<PaginatedResponse<CompanyItem>> {
    return realGet("/api/companies", params)
  },

  // ---------------------------------------------------------------------------
  // People
  // ---------------------------------------------------------------------------

  listPeople() {
    return realGet("/api/people")
  },

  createPerson(input: Record<string, unknown>): Promise<Record<string, unknown>> {
    return realPost("/api/people", input)
  },

  getPersonMasterList(): Promise<Record<string, unknown>[]> {
    return realGet("/api/people/master")
  },

  getPersonSpecter(personId: string): Promise<Record<string, unknown>> {
    return realGet(`/api/people/${personId}/specter`)
  },

  refreshPerson(personId: string, body?: Record<string, unknown>): Promise<Record<string, unknown>> {
    return realPost(`/api/people/${personId}/refresh`, body)
  },

  deactivatePerson(personId: string): Promise<Record<string, unknown>> {
    return realPost(`/api/people/${personId}/deactivate`)
  },

  activatePerson(personId: string): Promise<Record<string, unknown>> {
    return realPost(`/api/people/${personId}/activate`)
  },

  // ---------------------------------------------------------------------------
  // Investors
  // ---------------------------------------------------------------------------

  listInvestors() {
    return realGet("/api/investors")
  },

  listAgentRuns(filters?: import("./IDataAdapter").AgentRunFilters) {
    const p: Record<string, string> = {}
    if (filters) for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== "" && v !== false) p[k] = String(v)
    }
    return realGet("/api/agent-runs", Object.keys(p).length ? p : undefined) as Promise<
      import("./IDataAdapter").AgentRunItem[]
    >
  },

  getAgentRun(agentRunId: string, reveal?: boolean) {
    return realGet(`/api/agent-runs/${agentRunId}`, reveal ? { reveal: "1" } : undefined) as Promise<
      import("./IDataAdapter").AgentRunDetail
    >
  },

  getAgentRunLineage(agentRunId: string) {
    return realGet(`/api/agent-runs/${agentRunId}/lineage`) as Promise<
      import("./IDataAdapter").AgentRunLineageRecord
    >
  },

  listAgentToolCalls(reveal?: boolean) {
    return realGet("/api/agent-tool-calls", reveal ? { reveal: "1" } : undefined) as Promise<
      import("./IDataAdapter").AgentToolCallItem[]
    >
  },

  runAgent(input: string, dryRun?: boolean) {
    return realPost("/api/agent-runs/run", { input, dry_run: !!dryRun }) as Promise<
      import("./IDataAdapter").AgentRunItem
    >
  },

  preflightAgentRun(input: string, agentSlug?: string, dryRun?: boolean) {
    return realPost("/api/agent-runs/preflight", { input, agent_slug: agentSlug, dry_run: !!dryRun }) as Promise<
      import("./IDataAdapter").PreflightReport
    >
  },

  listAgentProvenance(filters?: { entity_type?: string; entity_id?: string; agent?: string; run?: string }) {
    return realGet("/api/agent-provenance", filters as Record<string, string> | undefined) as Promise<
      import("./IDataAdapter").AgentProvenanceItem[]
    >
  },

  listAgentProposedWrites(filters?: { run?: string; status?: string }) {
    return realGet("/api/agent-proposed-writes", filters as Record<string, string> | undefined) as Promise<
      import("./IDataAdapter").AgentProposedWriteItem[]
    >
  },

  listAgentWriteLedger(filters?: { run?: string; entity_type?: string; entity_id?: string }) {
    return realGet("/api/agent-write-ledger", filters as Record<string, string> | undefined) as Promise<
      import("./IDataAdapter").AgentWriteLedgerItem[]
    >
  },

  listAgentPolicies(agentSlug?: string) {
    return realGet("/api/agent-policies", agentSlug ? { agent: agentSlug } : undefined) as Promise<
      import("./IDataAdapter").AgentPolicyItem[]
    >
  },
  createAgentPolicy(input: Record<string, unknown>) {
    return realPost("/api/agent-policies", input) as Promise<import("./IDataAdapter").AgentPolicyItem>
  },
  updateAgentPolicy(policyId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-policies/${policyId}`, input) as Promise<import("./IDataAdapter").AgentPolicyItem>
  },
  deleteAgentPolicy(policyId: string) {
    return realDelete(`/api/agent-policies/${policyId}`) as Promise<void>
  },

  getAgentEffectiveConfig(slug: string) {
    return realGet(`/api/agents/${slug}/effective-config`) as Promise<import("./IDataAdapter").EffectiveConfig>
  },

  getAgentQueue() {
    return realGet("/api/agent-queue") as Promise<import("./IDataAdapter").QueueSnapshot>
  },

  retryAgentRun(agentRunId: string, resume?: boolean) {
    return realPost(`/api/agent-runs/${agentRunId}/retry`, { resume }) as Promise<import("./IDataAdapter").AgentRunItem>
  },
  setRunRecovery(agentRunId: string, input: { resolved?: boolean; suppressed?: boolean }) {
    return realPost(`/api/agent-runs/${agentRunId}/recovery`, input) as Promise<import("./IDataAdapter").AgentRunItem>
  },
  listAgentRetryPolicies() {
    return realGet("/api/agent-retry-policies") as Promise<import("./IDataAdapter").AgentRetryPolicyItem[]>
  },
  createAgentRetryPolicy(input: Record<string, unknown>) {
    return realPost("/api/agent-retry-policies", input) as Promise<import("./IDataAdapter").AgentRetryPolicyItem>
  },
  updateAgentRetryPolicy(policyId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-retry-policies/${policyId}`, input) as Promise<import("./IDataAdapter").AgentRetryPolicyItem>
  },
  deleteAgentRetryPolicy(policyId: string) {
    return realDelete(`/api/agent-retry-policies/${policyId}`) as Promise<void>
  },

  listAgentEnvironments() {
    return realGet("/api/agent-environments") as Promise<import("./IDataAdapter").AgentEnvironmentItem[]>
  },
  createAgentEnvironment(input: Record<string, unknown>) {
    return realPost("/api/agent-environments", input) as Promise<import("./IDataAdapter").AgentEnvironmentItem>
  },
  updateAgentEnvironment(envId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-environments/${envId}`, input) as Promise<import("./IDataAdapter").AgentEnvironmentItem>
  },
  deleteAgentEnvironment(envId: string) {
    return realDelete(`/api/agent-environments/${envId}`) as Promise<void>
  },

  listAgentAccessRules(agentSlug?: string) {
    return realGet("/api/agent-access", agentSlug ? { agent: agentSlug } : undefined) as Promise<
      import("./IDataAdapter").AgentAccessRuleItem[]
    >
  },
  createAgentAccessRule(input: Record<string, unknown>) {
    return realPost("/api/agent-access", input) as Promise<import("./IDataAdapter").AgentAccessRuleItem>
  },
  updateAgentAccessRule(ruleId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-access/${ruleId}`, input) as Promise<import("./IDataAdapter").AgentAccessRuleItem>
  },
  deleteAgentAccessRule(ruleId: string) {
    return realDelete(`/api/agent-access/${ruleId}`) as Promise<void>
  },
  getAgentAccessMatrix(agentSlug?: string) {
    return realGet("/api/agent-access/matrix", agentSlug ? { agent: agentSlug } : undefined) as Promise<
      import("./IDataAdapter").AgentAccessMatrix
    >
  },

  listAgentTriggers(agentSlug?: string) {
    return realGet("/api/agent-triggers", agentSlug ? { agent: agentSlug } : undefined) as Promise<
      import("./IDataAdapter").AgentTriggerItem[]
    >
  },
  createAgentTrigger(input: Record<string, unknown>) {
    return realPost("/api/agent-triggers", input) as Promise<import("./IDataAdapter").AgentTriggerItem>
  },
  updateAgentTrigger(triggerId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-triggers/${triggerId}`, input) as Promise<import("./IDataAdapter").AgentTriggerItem>
  },
  deleteAgentTrigger(triggerId: string) {
    return realDelete(`/api/agent-triggers/${triggerId}`) as Promise<void>
  },

  replayAgentRun(agentRunId: string, input?: string, dryRun?: boolean) {
    return realPost(`/api/agent-runs/${agentRunId}/replay`, { input, dry_run: !!dryRun }) as Promise<
      import("./IDataAdapter").AgentRunItem
    >
  },

  listAgentAlertRules() {
    return realGet("/api/agent-alert-rules") as Promise<import("./IDataAdapter").AgentAlertRuleItem[]>
  },
  createAgentAlertRule(input: Record<string, unknown>) {
    return realPost("/api/agent-alert-rules", input) as Promise<import("./IDataAdapter").AgentAlertRuleItem>
  },
  updateAgentAlertRule(ruleId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-alert-rules/${ruleId}`, input) as Promise<import("./IDataAdapter").AgentAlertRuleItem>
  },
  deleteAgentAlertRule(ruleId: string) {
    return realDelete(`/api/agent-alert-rules/${ruleId}`) as Promise<void>
  },
  listAgentAlertEvents() {
    return realGet("/api/agent-alert-events") as Promise<import("./IDataAdapter").AgentAlertEventItem[]>
  },

  listAgentGuardrails() {
    return realGet("/api/agent-guardrails") as Promise<import("./IDataAdapter").AgentGuardrailItem[]>
  },
  createAgentGuardrail(input: Record<string, unknown>) {
    return realPost("/api/agent-guardrails", input) as Promise<import("./IDataAdapter").AgentGuardrailItem>
  },
  updateAgentGuardrail(guardrailId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-guardrails/${guardrailId}`, input) as Promise<import("./IDataAdapter").AgentGuardrailItem>
  },
  deleteAgentGuardrail(guardrailId: string) {
    return realDelete(`/api/agent-guardrails/${guardrailId}`) as Promise<void>
  },
  listAgentGuardrailEvents() {
    return realGet("/api/agent-guardrail-events") as Promise<import("./IDataAdapter").AgentGuardrailEventItem[]>
  },
  getAgentGuardrailLayers(agentSlug?: string) {
    return realGet("/api/agent-guardrail-layers", agentSlug ? { agent: agentSlug } : undefined) as Promise<import("./IDataAdapter").GuardrailLayers>
  },
  simulateAgentGuardrails(text: string, phase?: string, agentSlug?: string) {
    return realPost("/api/agent-guardrails/simulate", { text, phase, agent: agentSlug }) as Promise<import("./IDataAdapter").GuardrailSimulation>
  },
  bulkAgentAction(target: "agent" | "run", action: string, ids: string[], params?: Record<string, unknown>) {
    return realPost("/api/agent-bulk-actions", { target, action, ids, params }) as Promise<import("./IDataAdapter").BulkActionResult>
  },
  listAgentScorecards() {
    return realGet("/api/agent-scorecards") as Promise<import("./IDataAdapter").AgentScorecard[]>
  },
  getFleetRecommendations() {
    return realGet("/api/agent-recommendations") as Promise<import("./IDataAdapter").FleetRecommendations>
  },
  getAgentDependencyMap() {
    return realGet("/api/agent-dependency-map") as Promise<import("./IDataAdapter").DependencyMap>
  },
  getAgentPersonas() {
    return realGet("/api/agent-personas") as Promise<import("./IDataAdapter").PersonaMap>
  },
  listAgentIncidents(statusFilter?: string) {
    return realGet("/api/agent-incidents", statusFilter ? { status: statusFilter } : undefined) as Promise<import("./IDataAdapter").AgentIncidentItem[]>
  },
  getAgentIncidentStats() {
    return realGet("/api/agent-incidents/stats") as Promise<import("./IDataAdapter").IncidentStats>
  },
  getAgentIncident(incidentId: string) {
    return realGet(`/api/agent-incidents/${incidentId}`) as Promise<import("./IDataAdapter").AgentIncidentItem>
  },
  createAgentIncident(input: Record<string, unknown>) {
    return realPost("/api/agent-incidents", input) as Promise<import("./IDataAdapter").AgentIncidentItem>
  },
  updateAgentIncident(incidentId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-incidents/${incidentId}`, input) as Promise<import("./IDataAdapter").AgentIncidentItem>
  },
  deleteAgentIncident(incidentId: string) {
    return realDelete(`/api/agent-incidents/${incidentId}`) as Promise<void>
  },
  addIncidentUpdate(incidentId: string, note?: string, statusVal?: string) {
    return realPost(`/api/agent-incidents/${incidentId}/updates`, { note, status: statusVal }) as Promise<import("./IDataAdapter").AgentIncidentItem>
  },
  listSandboxScenarios(agentSlug?: string) {
    return realGet("/api/agent-sandbox-scenarios", agentSlug ? { agent: agentSlug } : undefined) as Promise<import("./IDataAdapter").AgentSandboxScenarioItem[]>
  },
  createSandboxScenario(input: Record<string, unknown>) {
    return realPost("/api/agent-sandbox-scenarios", input) as Promise<import("./IDataAdapter").AgentSandboxScenarioItem>
  },
  updateSandboxScenario(scenarioId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-sandbox-scenarios/${scenarioId}`, input) as Promise<import("./IDataAdapter").AgentSandboxScenarioItem>
  },
  deleteSandboxScenario(scenarioId: string) {
    return realDelete(`/api/agent-sandbox-scenarios/${scenarioId}`) as Promise<void>
  },
  runSandboxScenario(scenarioId: string) {
    return realPost(`/api/agent-sandbox-scenarios/${scenarioId}/run`, {}) as Promise<import("./IDataAdapter").AgentSandboxScenarioItem>
  },

  listAgentDatasets() {
    return realGet("/api/agent-datasets") as Promise<import("./IDataAdapter").AgentDatasetItem[]>
  },
  getAgentDataset(datasetId: string) {
    return realGet(`/api/agent-datasets/${datasetId}`) as Promise<import("./IDataAdapter").AgentDatasetDetail>
  },
  createAgentDataset(input: Record<string, unknown>) {
    return realPost("/api/agent-datasets", input) as Promise<import("./IDataAdapter").AgentDatasetItem>
  },
  deleteAgentDataset(datasetId: string) {
    return realDelete(`/api/agent-datasets/${datasetId}`) as Promise<void>
  },
  addAgentDatasetItem(datasetId: string, input: Record<string, unknown>) {
    return realPost(`/api/agent-datasets/${datasetId}`, input) as Promise<import("./IDataAdapter").AgentDatasetItemRow>
  },
  listAgentEvals() {
    return realGet("/api/agent-evals") as Promise<import("./IDataAdapter").AgentEvalItem[]>
  },
  createAgentEval(input: Record<string, unknown>) {
    return realPost("/api/agent-evals", input) as Promise<import("./IDataAdapter").AgentEvalItem>
  },

  listAgentFeedback(filters?: { run?: string; agent?: string }) {
    return realGet("/api/agent-feedback", filters as Record<string, string> | undefined) as Promise<import("./IDataAdapter").AgentFeedbackItem[]>
  },
  submitAgentFeedback(input: Record<string, unknown>) {
    return realPost("/api/agent-feedback", input) as Promise<import("./IDataAdapter").AgentFeedbackItem>
  },

  diffAgentRuns(aId: string, bId: string) {
    return realGet("/api/agent-runs/diff", { a: aId, b: bId }) as Promise<import("./IDataAdapter").RunDiff>
  },

  listAgentEdges() {
    return realGet("/api/agent-edges") as Promise<import("./IDataAdapter").AgentEdgeItem[]>
  },
  createAgentEdge(input: Record<string, unknown>) {
    return realPost("/api/agent-edges", input) as Promise<import("./IDataAdapter").AgentEdgeItem>
  },
  updateAgentEdge(edgeId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-edges/${edgeId}`, input) as Promise<import("./IDataAdapter").AgentEdgeItem>
  },
  deleteAgentEdge(edgeId: string) {
    return realDelete(`/api/agent-edges/${edgeId}`) as Promise<void>
  },

  listAgentKnowledge() {
    return realGet("/api/agent-knowledge") as Promise<import("./IDataAdapter").AgentKnowledgeSourceItem[]>
  },
  createAgentKnowledge(input: Record<string, unknown>) {
    return realPost("/api/agent-knowledge", input) as Promise<import("./IDataAdapter").AgentKnowledgeSourceItem>
  },
  updateAgentKnowledge(sourceId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-knowledge/${sourceId}`, input) as Promise<import("./IDataAdapter").AgentKnowledgeSourceItem>
  },
  reindexAgentKnowledge(sourceId: string) {
    return realPost(`/api/agent-knowledge/${sourceId}/reindex`, {}) as Promise<import("./IDataAdapter").AgentKnowledgeSourceItem>
  },
  deleteAgentKnowledge(sourceId: string) {
    return realDelete(`/api/agent-knowledge/${sourceId}`) as Promise<void>
  },

  getAgentCostInsights(window?: string) {
    return realGet("/api/agent-cost-insights", window ? { window } : undefined) as Promise<import("./IDataAdapter").CostInsights>
  },

  listAgentTemplates() {
    return realGet("/api/agent-templates") as Promise<import("./IDataAdapter").AgentTemplateItem[]>
  },
  createAgentTemplate(input: Record<string, unknown>) {
    return realPost("/api/agent-templates", input) as Promise<import("./IDataAdapter").AgentTemplateItem>
  },
  deleteAgentTemplate(templateId: string) {
    return realDelete(`/api/agent-templates/${templateId}`) as Promise<void>
  },
  instantiateAgentTemplate(templateId: string, slug: string, name?: string) {
    return realPost(`/api/agent-templates/${templateId}/instantiate`, { slug, name }) as Promise<import("./IDataAdapter").AgentRegistryItem>
  },

  listAgentDigests() {
    return realGet("/api/agent-digests") as Promise<import("./IDataAdapter").AgentDigestItem[]>
  },
  createAgentDigest(input: Record<string, unknown>) {
    return realPost("/api/agent-digests", input) as Promise<import("./IDataAdapter").AgentDigestItem>
  },
  updateAgentDigest(digestId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-digests/${digestId}`, input) as Promise<import("./IDataAdapter").AgentDigestItem>
  },
  deleteAgentDigest(digestId: string) {
    return realDelete(`/api/agent-digests/${digestId}`) as Promise<void>
  },
  listAgentDigestEditions(digestId?: string) {
    return realGet("/api/agent-digest-editions", digestId ? { digest: digestId } : undefined) as Promise<import("./IDataAdapter").AgentDigestEditionItem[]>
  },

  stopAgentRun(agentRunId: string) {
    return realPost(`/api/agent-runs/${agentRunId}/stop`, {}) as Promise<
      import("./IDataAdapter").AgentRunItem
    >
  },

  overrideRunBudget(agentRunId: string, input: { step_budget?: number; cost_budget_usd?: number }) {
    return realPost(`/api/agent-runs/${agentRunId}/budget-override`, input) as Promise<
      import("./IDataAdapter").AgentRunItem
    >
  },

  getAgentsForEntity(entityType: string, entityId: string) {
    return realGet("/api/agents/for-entity", { entity_type: entityType, entity_id: entityId }) as Promise<
      import("./IDataAdapter").AgentRunItem[]
    >
  },

  listAgents() {
    return realGet("/api/agents") as Promise<
      import("./IDataAdapter").AgentRegistryItem[]
    >
  },

  getAgentConfig(slug: string) {
    return realGet(`/api/agents/${slug}/config`) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  createAgent(input: Record<string, unknown>) {
    return realPost("/api/agents", input) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  updateAgent(slug: string, input: Record<string, unknown>) {
    return realPatch(`/api/agents/${slug}/config`, input) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  deleteAgent(slug: string) {
    return realDelete(`/api/agents/${slug}/config`) as Promise<void>
  },

  markAgentReviewed(slug: string, note?: string) {
    return realPost(`/api/agents/${slug}/review`, note ? { note } : {}) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  getAgentToolStats() {
    return realGet("/api/agents/tool-stats").then(
      (r) => ((r as { rows?: import("./IDataAdapter").AgentToolStatRow[] }).rows ?? []),
    )
  },

  getAgentUsage(groupBy: string) {
    return realGet("/api/agents/usage", { group_by: groupBy }) as Promise<
      import("./IDataAdapter").AgentUsageResponse
    >
  },

  getAgentVersions(slug: string) {
    return realGet(`/api/agents/${slug}/versions`) as Promise<
      import("./IDataAdapter").AgentConfigVersionItem[]
    >
  },

  rollbackAgent(slug: string, versionNum: number) {
    return realPost(`/api/agents/${slug}/rollback/${versionNum}`, {}) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  setAgentLifecycle(slug: string, input: { lifecycle_stage?: string; canary_percent?: number; note?: string }) {
    return realPost(`/api/agents/${slug}/lifecycle`, input) as Promise<
      import("./IDataAdapter").AgentRegistryItem
    >
  },

  listAgentThreads(agentSlug?: string) {
    return realGet("/api/agent-threads", agentSlug ? { agent: agentSlug } : undefined) as Promise<
      import("./IDataAdapter").AgentThreadItem[]
    >
  },

  getAgentThread(threadId: string) {
    return realGet(`/api/agent-threads/${threadId}`) as Promise<
      import("./IDataAdapter").AgentThreadDetail
    >
  },

  listAgentArtifacts(filters?: { run?: string; agent?: string; entity_type?: string; entity_id?: string }) {
    return realGet("/api/agent-artifacts", filters as Record<string, string> | undefined) as Promise<
      import("./IDataAdapter").AgentArtifactItem[]
    >
  },

  getAgentArtifact(artifactId: string) {
    return realGet(`/api/agent-artifacts/${artifactId}`) as Promise<
      import("./IDataAdapter").AgentArtifactDetail
    >
  },

  listAgentQuotas() {
    return realGet("/api/agent-quotas") as Promise<
      import("./IDataAdapter").AgentQuotaItem[]
    >
  },

  createAgentQuota(input: Record<string, unknown>) {
    return realPost("/api/agent-quotas", input) as Promise<
      import("./IDataAdapter").AgentQuotaItem
    >
  },

  updateAgentQuota(quotaId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-quotas/${quotaId}`, input) as Promise<
      import("./IDataAdapter").AgentQuotaItem
    >
  },

  deleteAgentQuota(quotaId: string) {
    return realDelete(`/api/agent-quotas/${quotaId}`) as Promise<void>
  },

  listAgentSLAs() {
    return realGet("/api/agent-slas") as Promise<import("./IDataAdapter").AgentSLAItem[]>
  },
  createAgentSLA(input: Record<string, unknown>) {
    return realPost("/api/agent-slas", input) as Promise<import("./IDataAdapter").AgentSLAItem>
  },
  updateAgentSLA(slaId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-slas/${slaId}`, input) as Promise<import("./IDataAdapter").AgentSLAItem>
  },
  deleteAgentSLA(slaId: string) {
    return realDelete(`/api/agent-slas/${slaId}`) as Promise<void>
  },

  listAgentTools() {
    return realGet("/api/agent-tools") as Promise<import("./IDataAdapter").AgentToolItem[]>
  },
  createAgentTool(input: Record<string, unknown>) {
    return realPost("/api/agent-tools", input) as Promise<import("./IDataAdapter").AgentToolItem>
  },
  updateAgentTool(slug: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-tools/${slug}`, input) as Promise<import("./IDataAdapter").AgentToolItem>
  },
  deleteAgentTool(slug: string) {
    return realDelete(`/api/agent-tools/${slug}`) as Promise<void>
  },

  listAgentCredentials() {
    return realGet("/api/agent-credentials") as Promise<import("./IDataAdapter").AgentCredentialItem[]>
  },
  createAgentCredential(input: Record<string, unknown>) {
    return realPost("/api/agent-credentials", input) as Promise<import("./IDataAdapter").AgentCredentialItem>
  },
  updateAgentCredential(credId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-credentials/${credId}`, input) as Promise<import("./IDataAdapter").AgentCredentialItem>
  },
  rotateAgentCredential(credId: string) {
    return realPost(`/api/agent-credentials/${credId}/rotate`, {}) as Promise<import("./IDataAdapter").AgentCredentialItem>
  },
  deleteAgentCredential(credId: string) {
    return realDelete(`/api/agent-credentials/${credId}`) as Promise<void>
  },

  listAgentApprovals(statusFilter?: string) {
    return realGet("/api/agent-approvals", statusFilter ? { status: statusFilter } : undefined) as Promise<import("./IDataAdapter").AgentApprovalItem[]>
  },
  createAgentApproval(input: Record<string, unknown>) {
    return realPost("/api/agent-approvals", input) as Promise<import("./IDataAdapter").AgentApprovalItem>
  },
  decideAgentApproval(approvalId: string, decision: string, note?: string) {
    return realPost(`/api/agent-approvals/${approvalId}/decision`, { decision, note }) as Promise<import("./IDataAdapter").AgentApprovalItem>
  },

  listAgentApprovalRules() {
    return realGet("/api/agent-approval-rules") as Promise<import("./IDataAdapter").AgentApprovalRuleItem[]>
  },
  createAgentApprovalRule(input: Record<string, unknown>) {
    return realPost("/api/agent-approval-rules", input) as Promise<import("./IDataAdapter").AgentApprovalRuleItem>
  },
  updateAgentApprovalRule(ruleId: string, input: Record<string, unknown>) {
    return realPatch(`/api/agent-approval-rules/${ruleId}`, input) as Promise<import("./IDataAdapter").AgentApprovalRuleItem>
  },
  deleteAgentApprovalRule(ruleId: string) {
    return realDelete(`/api/agent-approval-rules/${ruleId}`) as Promise<void>
  },

  createInvestor(input: Record<string, unknown>): Promise<Record<string, unknown>> {
    return realPost("/api/investors", input)
  },

  getInvestorMasterList(): Promise<Record<string, unknown>[]> {
    return realGet("/api/investors/master")
  },

  getInvestorSpecter(investorId: string): Promise<Record<string, unknown>> {
    return realGet(`/api/investors/${investorId}/specter`)
  },

  refreshInvestor(investorId: string): Promise<Record<string, unknown>> {
    return realPost(`/api/investors/${investorId}/refresh`)
  },

  deactivateInvestor(investorId: string): Promise<Record<string, unknown>> {
    return realPost(`/api/investors/${investorId}/deactivate`)
  },

  activateInvestor(investorId: string): Promise<Record<string, unknown>> {
    return realPost(`/api/investors/${investorId}/activate`)
  },

  // ---------------------------------------------------------------------------
  // Export Jobs
  // ---------------------------------------------------------------------------

  createExportJob(input: CreateExportJobInput): Promise<ExportJobItem> {
    return realPost("/api/export_jobs", input)
  },

  listExportJobs(
    params?: ListExportJobsParams
  ): Promise<PaginatedResponse<ExportJobItem>> {
    return realGet("/api/export_jobs", params)
  },

  downloadExport(exportJobId: string) {
    return realGet(`/api/export_jobs/${exportJobId}/download`)
  },

  // ---------------------------------------------------------------------------
  // Jobs Monitor
  // ---------------------------------------------------------------------------

  listJobs(params?: ListJobsParams): Promise<PaginatedResponse<JobItem>> {
    return realGet("/api/jobs", params)
  },

  // ---------------------------------------------------------------------------
  // Insights
  // ---------------------------------------------------------------------------

  getCompaniesInsights(): Promise<CompaniesInsightsSummary> {
    return realGet("/api/insights/companies")
  },

  getCompaniesActivity(): Promise<CompaniesRecentActivityItem[]> {
    return realGet("/api/insights/companies/activity")
  },

  // ---------------------------------------------------------------------------
  // Companies List (page-specific)
  // ---------------------------------------------------------------------------

  getCompanyList(params?: ListCompanyListParams): Promise<CompanyListItem[]> {
    return realGet("/api/companies", params)
  },

  getCompanyMasterList(): Promise<Record<string, unknown>[]> {
    return realGet("/api/companies/master")
  },

  getCompanySpecter(companyId: number): Promise<Record<string, unknown>> {
    return realGet(`/api/companies/${companyId}/specter`)
  },

  refreshCompany(companyId: number): Promise<Record<string, unknown>> {
    return realPost(`/api/companies/${companyId}/refresh`)
  },

  deactivateCompany(companyId: number): Promise<Record<string, unknown>> {
    return realPost(`/api/companies/${companyId}/deactivate`)
  },

  activateCompany(companyId: number): Promise<Record<string, unknown>> {
    return realPost(`/api/companies/${companyId}/activate`)
  },

  getCompanyBySlug(slug: string): Promise<CompanyDetails | null> {
    return realGet(`/api/companies/${slug}`)
  },

  getCompanyMetrics(slug: string): Promise<DashboardMetric[]> {
    return realGet(`/api/companies/${slug}/metrics`)
  },

  // ---------------------------------------------------------------------------
  // Import Jobs
  // ---------------------------------------------------------------------------

  createImportJob(input: CreateImportJobInput): Promise<ImportJobItem> {
    return realPost("/api/import_jobs", input)
  },

  listImportJobs(): Promise<ImportJobItem[]> {
    return realGet("/api/import_jobs")
  },

  async downloadImportFile(importJobId: string): Promise<void> {
    const { API_BASE_URL, createHeaders } = await import("../apiClient")
    const { getApiToken } = await import("../authContext")
    const token = await getApiToken()
    const response = await fetch(
      `${API_BASE_URL}/api/import_jobs/${importJobId}/download`,
      { headers: createHeaders(token ?? undefined) },
    )
    if (!response.ok) throw new Error("Download failed")
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `import_${importJobId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  },

  // ---------------------------------------------------------------------------
  // Ingestion Jobs
  // ---------------------------------------------------------------------------

  createIngestionJob(input: CreateIngestionJobInput) {
    return realPost("/api/ingestion_jobs", input)
  },

  listIngestionJobs() {
    return realGet("/api/ingestion_jobs")
  },

  listIngestionJobApiCalls(ingestionJobId: string) {
    return realGet(
      `/api/ingestion_jobs/${encodeURIComponent(ingestionJobId)}/api_calls`,
    ) as Promise<import("./IDataAdapter").SpecterApiCallItem[]>
  },

  // ---------------------------------------------------------------------------
  // Admin Master Lists
  // ---------------------------------------------------------------------------

  getMasterCompanies(
    params?: ListMasterCompaniesParams
  ): Promise<MasterCompanyItem[]> {
    return realGet("/api/companies/master", params)
  },

  getUsers(params?: ListUsersParams): Promise<UserItem[]> {
    return realGet("/api/admin/users", params)
  },

  // ---------------------------------------------------------------------------
  // Unicorn Prediction (UP)
  // ---------------------------------------------------------------------------

  getUnicornPredictionList(runId?: string) {
    return realGet(`/api/unicorn-predictions${runId ? `?run=${runId}` : ""}`) as Promise<UnicornPredictionItem[]>
  },

  getCompanyUnicornScore(companyId: number) {
    return realGet(`/api/companies/${companyId}/unicorn-score`) as Promise<
      import("./IDataAdapter").CompanyUnicornScore
    >
  },

  getCompanyUnicornScoreComparison(companyId: number) {
    return realGet(
      `/api/companies/${companyId}/unicorn-score-comparison`,
    ) as Promise<import("./IDataAdapter").CompanyUnicornScoreComparison>
  },

  getUnicornPredictionSummary(runId?: string) {
    return realGet(`/api/unicorn-predictions/summary${runId ? `?run=${runId}` : ""}`) as Promise<UnicornPredictionSummary>
  },

  getUnicornCapturePerformance(runId?: string) {
    return realGet(`/api/unicorn-predictions/performance${runId ? `?run=${runId}` : ""}`) as Promise<UnicornCapturePerformance>
  },

  refreshUnicornCapture() {
    return realPost("/api/unicorn-predictions/performance") as Promise<{
      started: boolean; refresh_state: UnicornCapturePerformance["refresh_state"]
    }>
  },

  getUnicornPredictionAnalytics(runId?: string) {
    return realGet(`/api/unicorn-predictions/analytics${runId ? `?run=${runId}` : ""}`) as Promise<
      import("./IDataAdapter").UnicornPredictionAnalytics
    >
  },

  getUnicornPredictionMethodology() {
    return realGet("/api/unicorn-predictions/methodology") as Promise<UnicornPredictionMethodology>
  },

  getUnicornMethodologyVersions() {
    return realGet("/api/unicorn-predictions/methodology/versions") as Promise<
      import("./IDataAdapter").UnicornMethodologyVersionsResponse
    >
  },

  getUnicornMethodologyVersion(versionTag: string) {
    return realGet(
      `/api/unicorn-predictions/methodology/versions/${encodeURIComponent(versionTag)}`,
    ) as Promise<import("./IDataAdapter").UnicornMethodologyVersionDetail>
  },

  startUnicornRescore(opts?: { version_tag?: string; run_name?: string; notes?: string }) {
    return realPost("/api/unicorn-predictions/rescore", opts ?? {}) as Promise<
      import("./IDataAdapter").UnicornRescoreJob
    >
  },

  getUnicornRuns() {
    return realGet("/api/unicorn-predictions/runs") as Promise<
      import("./IDataAdapter").UnicornRunsRegistry
    >
  },

  getUnicornRescoreJob(jobId: string) {
    return realGet(
      `/api/unicorn-predictions/rescore/${encodeURIComponent(jobId)}`,
    ) as Promise<import("./IDataAdapter").UnicornRescoreJob>
  },

  // ---------------------------------------------------------------------------
  // Entity tags (Company / Investor / Person)
  // ---------------------------------------------------------------------------

  listEntityTags(params: {
    entity_type?: import("./IDataAdapter").EntityTagType
    entity_id?: string
    tag?: string
  }) {
    const q = new URLSearchParams()
    if (params.entity_type) q.set("entity_type", params.entity_type)
    if (params.entity_id) q.set("entity_id", params.entity_id)
    if (params.tag) q.set("tag", params.tag)
    const qs = q.toString()
    return realGet(`/api/tags${qs ? `?${qs}` : ""}`) as Promise<
      import("./IDataAdapter").EntityTagItem[]
    >
  },

  addEntityTag(input) {
    return realPost("/api/tags", input) as Promise<
      import("./IDataAdapter").EntityTagItem
    >
  },

  deleteEntityTag(entityTagId: string) {
    return realDelete<void>(`/api/tags/${encodeURIComponent(entityTagId)}`)
  },

  bulkAddEntityTags(input) {
    return realPost("/api/tags/bulk", input) as Promise<
      import("./IDataAdapter").EntityTagBulkSummary
    >
  },

  // ---------------------------------------------------------------------------
  // Phase 4 — Specter expansion sub-tabs
  // ---------------------------------------------------------------------------

  getCompanyFundingRounds(companyId) {
    return realGet(`/api/companies/${companyId}/funding-rounds`) as Promise<
      import("./IDataAdapter").CompanyFundingRoundItem[]
    >
  },
  getCompanyAcquisitions(companyId) {
    return realGet(`/api/companies/${companyId}/acquisitions`) as Promise<
      import("./IDataAdapter").CompanyAcquisitionItem[]
    >
  },
  getCompanyIPOs(companyId) {
    return realGet(`/api/companies/${companyId}/ipos`) as Promise<
      import("./IDataAdapter").CompanyIPOItem[]
    >
  },
  getCompanyNewsSignals(companyId, limit) {
    const q = limit ? `?limit=${limit}` : ""
    return realGet(`/api/companies/${companyId}/news-signals${q}`) as Promise<
      import("./IDataAdapter").CompanyNewsSignalItem[]
    >
  },
  getCompanyRevenueSignals(companyId) {
    return realGet(`/api/companies/${companyId}/revenue-signals`) as Promise<
      import("./IDataAdapter").CompanyRevenueSignalItem[]
    >
  },
  getCompanySimilar(companyId) {
    return realGet(`/api/companies/${companyId}/similar`) as Promise<
      import("./IDataAdapter").CompanySimilarItem[]
    >
  },
  getInvestorEnrichment(investorId) {
    return realGet(`/api/investors/${encodeURIComponent(investorId)}/enrichment`) as Promise<
      import("./IDataAdapter").InvestorEnrichmentResponse
    >
  },
  getInvestorPortfolio(investorId) {
    return realGet(`/api/investors/${encodeURIComponent(investorId)}/portfolio`) as Promise<
      import("./IDataAdapter").InvestorPortfolioItem[]
    >
  },
  getInvestorTrackRecord(investorId) {
    return realGet(`/api/investors/${encodeURIComponent(investorId)}/track-record`) as Promise<
      import("./IDataAdapter").InvestorTrackRecord
    >
  },
  getInvestorPortfolioNews(investorId, limit) {
    const q = limit ? `?limit=${limit}` : ""
    return realGet(`/api/investors/${encodeURIComponent(investorId)}/news${q}`) as Promise<
      import("./IDataAdapter").CompanyNewsSignalItem[]
    >
  },
  getPhase4Insights() {
    return realGet("/api/insights/phase4") as Promise<
      import("./IDataAdapter").Phase4InsightsResponse
    >
  },
  specterTextSearch(query: string) {
    return realPost("/api/specter/text-search", { query }) as Promise<unknown[]>
  },
  specterReverseEmail(email: string) {
    return realPost("/api/specter/reverse-email", { email }) as Promise<Record<string, unknown>>
  },
  getPersonExperienceHistory(personId: string) {
    return realGet(`/api/people/${encodeURIComponent(personId)}/experience-history`) as Promise<
      import("./IDataAdapter").PersonExperienceEntry[]
    >
  },
  getPersonEducationHistory(personId: string) {
    return realGet(`/api/people/${encodeURIComponent(personId)}/education-history`) as Promise<
      import("./IDataAdapter").PersonEducationEntry[]
    >
  },

  // ---------------------------------------------------------------------------
  // Identity
  // ---------------------------------------------------------------------------

  getMe() {
    return realGet("/api/me")
  },
}
